import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, Check, CreditCard, MessageCircle, ShieldCheck, Sparkles, X } from 'lucide-react';
import QRCode from 'qrcode';
import { activateFree, createOrder, getAccount, getTeamAccount, getCatalog, getOrder, hasLogin, type BillingAccount, type Catalog, type Product } from './payment-api';
import { useAuthUser } from './use-auth-user';
import AccountNav from './account-nav';

const OFFICE_URL = '/office.html';
const loginForPricing = (productId?: string) => {
  const query = new URLSearchParams(location.search);
  query.delete('order');
  if(productId)query.set('plan',productId);
  const returnTo = '/pricing' + (query.size ? '?' + query.toString() : '');
  return '/login?return_to=' + encodeURIComponent(returnTo);
};

const Brand = () => <a className="brand" href="/"><img className="brand-mark" src="/merrydonkey-assistant-logo.png" alt="" aria-hidden="true"/><span>merrydonkey</span></a>;
const money = (fen: string) => `¥${(Number(fen) / 100).toLocaleString('zh-CN', { maximumFractionDigits: 2 })}`;
const periodName: Record<string, string> = { none: '', month: '月付', quarter: '季付', year: '年付' };

function CheckoutModal({ product, catalog, teamId, teamName, onClose, onPaid }:{ product: Product; catalog: Catalog; teamId?: string; teamName?: string; onClose:()=>void; onPaid:()=>void }) {
  const [provider, setProvider] = useState<'alipay'|'wechat'>(()=>catalog.paymentProviders.alipay?'alipay':'wechat');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [qrImage, setQrImage] = useState('');
  const [orderId, setOrderId] = useState('');
  const [paid, setPaid] = useState(false);

  useEffect(()=>{
    let active=true;
    if(qrCode) void QRCode.toDataURL(qrCode,{width:220,margin:1,color:{dark:'#16251b',light:'#ffffff'}})
      .then(image=>{if(active)setQrImage(image);})
      .catch(()=>{if(active)setMessage('二维码生成失败，请关闭后重试');});
    return()=>{active=false;};
  },[qrCode]);
  useEffect(()=>{
    if(!paid)return;
    const timer=window.setTimeout(onClose,1800);
    return()=>window.clearTimeout(timer);
  },[paid,onClose]);
  useEffect(()=>{
    if(!orderId||!qrCode||paid)return;
    let stopped=false;
    let timer=0;
    let attempts=0;
    const check=async()=>{
      if(stopped)return;
      attempts++;
      try{
        const order=await getOrder(orderId);
        if(stopped)return;
        if(order.status==='paid'){
          setPaid(true);
          onPaid();
          return;
        }
        if(order.status==='closed'||order.status==='refunded'){
          setMessage(order.status==='closed'?'订单已关闭，请重新发起支付':'订单已退款');
          return;
        }
      }catch{
        // Temporary polling failures should not interrupt an otherwise valid QR payment.
      }
      if(stopped)return;
      if(attempts<200)timer=window.setTimeout(check,1500);
      else setMessage('支付状态同步超时，请刷新页面查看套餐与 Credits');
    };
    void check();
    return()=>{stopped=true;window.clearTimeout(timer);};
  },[orderId,qrCode,paid,onPaid]);

  const pay = async () => {
    if (!hasLogin()) { window.location.href = loginForPricing(product.id); return; }
    if (!catalog.paymentProviders[provider]) { setMessage(`${provider === 'alipay' ? '支付宝' : '微信'}商户能力正在开通，暂时无法支付`); return; }
    setBusy(true); setMessage('');
    try {
      const result = await createOrder(product.id, provider, teamId);
      if (result.checkout.type === 'redirect') window.location.href = result.checkout.value;
      else { setOrderId(result.order.id); setQrCode(result.checkout.value); }
    } catch (error) { setMessage(error instanceof Error ? error.message : '创建订单失败'); }
    finally { setBusy(false); }
  };

  return <div className="checkout-backdrop" onMouseDown={onClose}>
    <div className="checkout-modal" onMouseDown={(event)=>event.stopPropagation()}>
      <button className="modal-close" onClick={onClose} aria-label="关闭"><X/></button>
      <span className="checkout-kicker">{teamId ? `为团队 ${teamName} 充值` : '个人账号结算'}</span><h2>{product.name}{product.kind==='subscription'&&product.billingPeriod!=='none'?` · ${periodName[product.billingPeriod]}`:''}</h2>
      <div className="checkout-summary"><span>{Number(product.credits).toLocaleString()} Credits</span><b>{money(product.priceFen)}</b></div>
      {qrCode ? <div className={`qr-placeholder${paid?' payment-succeeded':''}`}>{paid?<><Check/><b>支付成功</b><p>套餐和 Credits 已到账，正在刷新页面…</p></>:<><MessageCircle/><b>微信扫码支付</b>{qrImage&&<img src={qrImage} alt="微信支付二维码"/>}<p>请使用微信扫描二维码完成支付，支付结果会自动同步。</p>{message&&<p className="checkout-error">{message}</p>}</>}</div> : <>
        <p className="provider-label">选择支付方式</p>
        <div className="provider-options">
          <button className={provider==='alipay'?'selected':''} disabled={busy||!catalog.paymentProviders.alipay} onClick={()=>setProvider('alipay')}><i className="alipay-icon">支</i><span><b>支付宝</b><small>{catalog.paymentProviders.alipay?'即时到账':'商户能力待开通'}</small></span><em/></button>
          <button className={provider==='wechat'?'selected':''} disabled={busy||!catalog.paymentProviders.wechat} onClick={()=>setProvider('wechat')}><i className="wechat-icon">微</i><span><b>微信支付</b><small>{catalog.paymentProviders.wechat?'扫码支付':'商户能力待开通'}</small></span><em/></button>
        </div>
        {message && <p className="checkout-error">{message}</p>}
        <button className="checkout-pay" disabled={busy||!catalog.paymentProviders[provider]} onClick={pay}>{busy?'正在创建订单…':hasLogin()?`确认支付 ${money(product.priceFen)}`:'登录后购买'}</button>
        <p className="checkout-safe"><ShieldCheck/>支付回调验签，Credits 到账可追溯</p>
      </>}
    </div>
  </div>;
}

type Term = 'month' | 'quarter' | 'year';
const TERMS: { id: Term; label: string; months: number }[] = [
  { id: 'month', label: '月付', months: 1 },
  { id: 'quarter', label: '季付', months: 3 },
  { id: 'year', label: '年付', months: 12 },
];
const TIERS = ['free', 'lite', 'pro', 'max'];
const creditsLabel: Record<string, string> = { none: '一次性', month: '每月', quarter: '每季度', year: '每年' };
const termMonths = (period: string) => TERMS.find(term => term.id === period)?.months ?? 1;
const savingPercent = (product: Product, monthly?: Product) => {
  if (!monthly || product.billingPeriod === 'month' || product.billingPeriod === 'none') return 0;
  const full = Number(monthly.priceFen) * termMonths(product.billingPeriod);
  return Math.floor((full - Number(product.priceFen)) / full * 100);
};

export default function PricingPage() {
  const query = new URLSearchParams(location.search);
  const teamId = query.get('team_id') || '';
  const teamName = query.get('team_name') || '当前团队';
  const loadAccount = useCallback(()=>teamId ? getTeamAccount(teamId) : getAccount(), [teamId]);
  const [catalog, setCatalog] = useState<Catalog>({ products: [], paymentProviders: { alipay: false, wechat: false } });
  const [selected, setSelected] = useState<Product|null>(null);
  const [term, setTerm] = useState<Term>('month');
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(true);
  const [catalogError, setCatalogError] = useState('');
  const [activating, setActivating] = useState(false);
  const activatingRef = useRef(false);
  const [account, setAccount] = useState<BillingAccount|null>(null);
  const [accountError, setAccountError] = useState('');
  const { user: authUser, ready: authReady } = useAuthUser();
  const loggedIn = Boolean(authUser);
  const handledPlan = useRef('');
  const subscriptions = useMemo(()=>catalog?.products.filter((item)=>item.kind==='subscription') ?? [],[catalog]);
  const topups = useMemo(()=>catalog?.products.filter((item)=>item.kind==='topup') ?? [],[catalog]);
  const tierProducts = (tier: string) => subscriptions.filter(product=>product.tier===tier);
  const monthlyOf = (tier: string) => tierProducts(tier).find(product=>product.billingPeriod==='month');
  const productFor = (tier: string) => {
    const products = tierProducts(tier);
    return products.find(product=>product.billingPeriod===term||product.billingPeriod==='none');
  };
  const visibleTiers = TIERS.filter(tier=>productFor(tier));
  const proMonthly = monthlyOf('pro');
  const termSaving = (id: Term) => {
    const product = tierProducts('pro').find(item=>item.billingPeriod===id);
    return product ? savingPercent(product, proMonthly) : 0;
  };

  useEffect(()=>{
    let active = true;
    void getCatalog().then(next=>{if(active)setCatalog(next);})
      .catch(()=>{if(active)setCatalogError('套餐加载失败，请刷新页面重试');})
      .finally(()=>{if(active)setLoading(false);});
    return ()=>{active=false;};
  },[]);
  useEffect(()=>{
    let active = true;
    setAccount(null);
    setAccountError('');
    if (loggedIn) {
      void loadAccount().then(next=>{if(active)setAccount(next);})
        .catch(error=>{if(active)setAccountError(error instanceof Error?error.message:'套餐信息加载失败');});
    } else {
      setSelected(null);
    }
    return ()=>{active=false;};
  },[authUser?.id, loggedIn, loadAccount]);
  const refreshAfterPayment=useCallback(()=>{
    setNotice('支付成功，套餐和 Credits 已到账');
    void loadAccount().then(next=>{setAccount(next);setAccountError('');})
      .catch(()=>setAccountError('套餐信息刷新失败，请刷新页面重试'));
  },[loadAccount]);
  useEffect(()=>{
    const id = new URLSearchParams(location.search).get('order');
    if (!id || !loggedIn) return;
    let stopped=false, count=0, timer=0;
    const check=async()=>{
      try {
        const order=await getOrder(id);
        if(stopped)return;
        if(order.status==='paid'){refreshAfterPayment();return;}
        if(order.status==='closed'||order.status==='refunded'){
          setNotice(order.status==='closed'?'订单已关闭':'订单已退款');return;
        }
      } catch { /* Retry transient failures. */ }
      if(stopped)return;
      if(++count<200)timer=window.setTimeout(check,1500);
      else setNotice('支付状态同步超时，请刷新页面查看套餐与 Credits');
    };
    void check();
    return ()=>{stopped=true;window.clearTimeout(timer);};
  },[loggedIn,refreshAfterPayment]);

  const currentProductId=account?.subscription?.product_id||'';
  const currentProduct=subscriptions.find(product=>product.id===currentProductId);
  const planState=(product:Product)=>teamId&&product.id==='free'?'team_unavailable':product.id===currentProductId?'current':product.id==='free'&&account?.freeActivated?'claimed':accountError?'unknown':!authReady||(loggedIn&&!account)?'pending':product.kind==='subscription'&&currentProduct&&product.planRank<=currentProduct.planRank?'lower':'available';

  const choose = async (product: Product) => {
    if (!authReady||activatingRef.current) return;
    if (!hasLogin()) { location.href=loginForPricing(product.id); return; }
    if (accountError) { setNotice('套餐信息加载失败，请刷新后重试'); return; }
    const state=planState(product);
    if(state!=='available') return;
    if (product.id !== 'free') { setSelected(product); return; }
    activatingRef.current=true; setActivating(true);
    try { const result=await activateFree(); setNotice(result.activated?`Free 套餐已开通，${Number(product.credits).toLocaleString()} Credits 已到账`:'你已经领取过 Free 套餐'); }
    catch(error){ setNotice(error instanceof Error?error.message:'开通失败'); }
    finally {
      try { setAccount(await loadAccount()); setAccountError(''); }
      catch { setAccountError('套餐信息刷新失败，请刷新页面重试'); }
      activatingRef.current=false; setActivating(false);
    }
  };

  useEffect(()=>{
    const productId=new URLSearchParams(location.search).get('plan');
    if(loading||(!account&&!accountError)||!productId||!hasLogin()||handledPlan.current===productId)return;
    handledPlan.current=productId;
    const product=catalog.products.find(item=>item.id===productId);
    if(!product)return;
    if(product.billingPeriod!=='none')setTerm(product.billingPeriod);
    if(planState(product)==='unknown'){setNotice('套餐信息加载失败，请刷新后重试');return;}
    if(planState(product)!=='available'){setNotice('当前账号不能选择该套餐，请选择更高等级的方案');return;}
    void choose(product);
  },[loading,catalog,account,accountError]);
  const formatDate=(value:string)=>new Intl.DateTimeFormat('zh-CN',{year:'numeric',month:'long',day:'numeric'}).format(new Date(value));
  const closeCheckout=useCallback(()=>setSelected(null),[]);
  const buttonLabel=(product:Product,state:string)=>activating&&product.id==='free'?'正在领取…':state==='team_unavailable'?'仅限个人账号':state==='pending'?'正在确认账号…':state==='current'?(product.id==='free'?'体验中':'当前套餐，到期后可续费'):state==='claimed'?'已领取':state==='lower'?'低于当前套餐':state==='unknown'?'套餐信息暂不可用':product.id==='free'?'免费领取':currentProduct&&currentProduct.id!=='free'?'升级到此方案':'购买此方案';

  const renderCard=(tier:string)=>{
    const product=productFor(tier);
    if(!product)return null;
    const state=planState(product);
    const featured=tier==='pro';
    const months=termMonths(product.billingPeriod);
    const monthly=monthlyOf(tier);
    const saving=savingPercent(product,monthly);
    return <article className={`plan-card ${featured?'featured':''} ${state}`} key={tier}>
      {state==='current'?<span className="current-badge">当前套餐</span>:featured&&<span className="popular">推荐</span>}
      <div className="plan-head"><span>{product.name}</span><p>{product.description}</p></div>
      <div className="plan-price"><b>{product.priceFen==='0'?'¥0':money(String(Math.ceil(Number(product.priceFen)/months/10)*10))}</b><em>{product.priceFen==='0'?'':'/ 月'}</em></div>
      <p className="plan-billing">{product.priceFen==='0'?'无需付费，注册即领':months===1?'按月购买，到期不自动扣费':<>{TERMS.find(item=>item.id===product.billingPeriod)?.label} {money(product.priceFen)}{monthly&&<s>{money(String(Number(monthly.priceFen)*months))}</s>}{saving>0&&<mark>省 {saving}%</mark>}</>}</p>
      <div className="plan-credit"><Sparkles/><span><b>{Number(product.credits).toLocaleString()}</b> Credits</span><small>{creditsLabel[product.billingPeriod]}</small></div>
      <button disabled={state!=='available'||activating} onClick={()=>choose(product)}>{buttonLabel(product,state)}</button>
      <ul>{product.features.map(feature=><li key={feature}><Check/>{feature}</li>)}</ul>
    </article>;
  };

  return <div className="pricing-page">
    <header className="site-header"><div className="container nav-wrap"><Brand/><nav><a href="/#products">产品</a><a className="active" href="/pricing">定价</a><a href="/#cases">案例</a><a href="/#faq">常见问题</a></nav><div className="nav-actions">{loggedIn?<AccountNav user={authUser} officeUrl={OFFICE_URL}/>:authReady&&<a className="login-link" href={loginForPricing()}>登录</a>}<a className="nav-cta" href={OFFICE_URL}>进入工作区</a></div></div></header>
    <main>
      <section className="pricing-hero"><div className="pricing-glow"/><div className="container"><a className="back-link" href="/"><ArrowLeft/>返回首页</a><span className="pricing-pill"><Sparkles/> 定价</span><h1>{teamId ? `为 ${teamName}` : '按你的工作节奏，'}<br/><span>选择刚刚好的方案。</span></h1><p>先免费体验，再按需订阅。所有方案都能使用全部工作台和 Agent 能力，区别只在可用的 Credits 数量。</p></div></section>
      {notice&&<div className="pricing-notice">{notice}<button onClick={()=>setNotice('')}><X/></button></div>}
      <section className="plans-section"><div className="container">
        {accountError&&<p role="alert" className="checkout-error">{accountError}</p>}
        {account&&<div className="current-account-plan"><div><span>当前套餐</span><b>{currentProduct?.name||(account.subscription?'已开通套餐':'未开通套餐')}</b>{account.subscription?.period_end&&<small>有效期至 {formatDate(account.subscription.period_end)}{currentProduct&&currentProduct.id!=='free'?'，到期后可续费':''}</small>}</div><div><span>可用 Credits</span><b>{Number(account.balance).toLocaleString()}</b><small>不够用可以<a href="#topup">单独加购</a></small></div><a href={OFFICE_URL}>返回工作台</a></div>}
        <div className="term-switch" role="tablist" aria-label="付费周期">{TERMS.map(item=>{const saving=termSaving(item.id);return <button key={item.id} role="tab" aria-selected={term===item.id} className={term===item.id?'selected':''} onClick={()=>setTerm(item.id)}>{item.label}{saving>0&&<small>省 {saving}%</small>}</button>;})}</div>
        {catalogError?<div role="alert" className="pricing-loading">{catalogError}</div>:loading?<div className="pricing-loading">正在加载套餐…</div>:<div className={`plan-grid cols-${visibleTiers.length}`}>{visibleTiers.map(renderCard)}</div>}
        <div className="billing-explain"><div><b>全部能力，按量区分</b><p>每个方案都能使用 PPT、Word、Excel 等全部工作台和 Agent，只是 Credits 数量不同。</p></div><div><b>随时升级，立即生效</b><p>升级后新套餐马上可用，原套餐剩余的 Credits 和有效期照常保留。</p></div><div><b>不自动扣费</b><p>所有套餐都是一次性购买，到期不会自动续费，需要时再买即可。</p></div></div>
      </div></section>
      <section className="topup-section" id="topup"><div className="container"><div className="topup-heading"><div><span>加油包</span><h2>Credits 不够用？随时加购</h2><p>加油包永久有效，不随套餐到期清零。系统会优先使用快到期的套餐 Credits。</p></div><div className="credit-equation"><CreditCard/><span><b>长期使用，订阅更划算</b><small>Pro 年付每 Credit 约 ¥0.07</small></span></div></div><div className="topup-grid">{topups.map(product=><button key={product.id} disabled={planState(product)!=='available'||activating} onClick={()=>choose(product)}><span><b>{Number(product.credits).toLocaleString()}</b><small>Credits · 永久有效</small></span><em>{money(product.priceFen)}</em><i>购买</i></button>)}</div></div></section>
      <section className="pricing-faq"><div className="container"><h2>关于套餐和 Credits</h2><div>
        <details><summary>Credits 是怎么消耗的？<span>+</span></summary><p>每次让 AI 生成或修改内容，都会按实际用到的模型和用量扣除 Credits。任务越复杂、内容越多，消耗越多。每一笔消耗都能在账单里查到。</p></details>
        <details><summary>套餐到期后，剩下的 Credits 怎么办？<span>+</span></summary><p>套餐内的 Credits 随套餐到期清零，单独购买的加油包永久有效。扣费时会优先使用快到期的 Credits。</p></details>
        <details><summary>可以提前续费或升级吗？<span>+</span></summary><p>同一档位到期后可以再次购买；随时可以升级到更高的档位，升级立即生效，原套餐剩余的 Credits 和有效期都会保留。</p></details>
        <details><summary>会自动扣费吗？<span>+</span></summary><p>不会。所有套餐都是一次性购买，到期不会自动续费。</p></details>
        <details><summary>支持哪些付款方式？<span>+</span></summary><p>支持支付宝和微信支付，到账后可在本页和工作台查看余额。</p></details>
      </div></div></section>
    </main>
    <footer><div className="container footer-bottom"><span>© 2026 merrydonkey</span><span>套餐清楚，消费透明。</span></div></footer>
    {selected&&<CheckoutModal product={selected} catalog={catalog} teamId={teamId||undefined} teamName={teamName} onClose={closeCheckout} onPaid={refreshAfterPayment}/>}
  </div>;
}
